/**
 * Voluntary Life & AD&D Transform Functions
 *
 * Handles bi-directional transformation between grid and API formats.
 */

import {
  DefinitionOfEarningsEnum,
  NotDisclosed,
  RfpVoluntaryLifeEmployeeBenefitTypeEnum,
  RfpVoluntaryLifeSpouseChildBenefitTypeEnum,
} from "@platform/openapi-types";

import {
  cleanupNotDisclosedBenefits,
  mapAgeReductionBandsFromApi,
  mapAgeReductionBandsToApi,
  shouldIncludeBenefitInApi,
  toExtendedStringField,
  transformAgeReductionScheduleFromApi,
  transformAgeReductionScheduleToApi,
} from "../shared/builders";
import { type BenefitTransformToApiOptions } from "../shared/transformOptions";

import {
  ReductionDetails,
  VoluntaryLifeBenefitsApi,
  VoluntaryLifeBenefitsGrid,
  VolLifeExtendedBenefitApi,
} from "./types";
import { VOLUNTARY_LIFE_VALID_FIELDS } from "./validFields";

type AgeBasedAmount = NonNullable<
  NonNullable<
    NonNullable<VoluntaryLifeBenefitsApi["employee"]>["guaranteeIssue"]
  >["ageBasedAmounts"]
>[number];

type ChildBenefitAmountByAge = NonNullable<
  NonNullable<VoluntaryLifeBenefitsApi["child"]>["benefitAmountsByAge"]
>[number];

type AmountOption = { amount: number };
type ChildBenefitType = string | undefined;
type ChildBenefitInputAmount = number | AmountOption[] | undefined;
type EmployeeBenefitsApi = NonNullable<VoluntaryLifeBenefitsApi["employee"]>;
type SpouseBenefitsApi = NonNullable<VoluntaryLifeBenefitsApi["spouse"]>;
type ChildBenefitsApi = NonNullable<VoluntaryLifeBenefitsApi["child"]>;

function hasEmployeeBenefits(benefits: VoluntaryLifeBenefitsGrid): boolean {
  return Boolean(
    benefits.benefitDescriptionEmployeeType ||
    benefits.benefitDescriptionEmployeeAmount ||
    benefits.benefitMaximumEmployeeType ||
    benefits.benefitMaximumEmployeeDollarAmount ||
    benefits.benefitMaximumEmployeeSalaryMultiplier ||
    benefits.guaranteeIssueEmployeeType ||
    benefits.guaranteeIssueEmployeeDollarAmount ||
    benefits.guaranteeIssueEmployeeSalaryMultiplier ||
    benefits.guaranteeIssueEmployeeAdditionalAmount ||
    benefits.guaranteeIssueEmployeeAgesDetails,
  );
}

function hasSpouseBenefits(benefits: VoluntaryLifeBenefitsGrid): boolean {
  return Boolean(
    benefits.benefitDescriptionSpouseType ||
    benefits.benefitDescriptionSpouseAmount ||
    benefits.benefitMaximumSpouseType ||
    benefits.benefitMaximumSpouseAmount ||
    benefits.guaranteeIssueSpouseType ||
    benefits.guaranteeIssueSpouseDollarAmount ||
    benefits.guaranteeIssueSpouseSalaryMultiplier ||
    benefits.guaranteeIssueSpouseAdditionalAmount ||
    benefits.guaranteeIssueSpouseAgesDetails ||
    benefits.amountNotToExceedSpouseType ||
    benefits.amountNotToExceedSpouseAmount,
  );
}

function hasChildBenefits(benefits: VoluntaryLifeBenefitsGrid): boolean {
  return Boolean(
    benefits.benefitDescriptionChildDependentAgesBirthTo14DaysType ||
    benefits.benefitDescriptionChildDependentAgesBirthTo14DaysAmount ||
    benefits.benefitDescriptionChildDependentAges15DaysTo6MonthsType ||
    benefits.benefitDescriptionChildDependentAges15DaysTo6MonthsAmount ||
    benefits.benefitDescriptionChildDependentAges6MonthsToLimitingAgeType ||
    benefits.benefitDescriptionChildDependentAges6MonthsToLimitingAgeAmount ||
    benefits.childLimitingAge ||
    benefits.benefitMaximumChildType ||
    benefits.benefitMaximumChildAmount ||
    benefits.amountNotToExceedChildType ||
    benefits.amountNotToExceedChildAmount ||
    benefits.studentStatusStudentLimitingAgeType ||
    (benefits.childBenefitAmountsByAge?.length ?? 0) > 0,
  );
}

function mapAgeBasedAmounts(
  ageDetails:
    | VoluntaryLifeBenefitsGrid["guaranteeIssueEmployeeAgesDetails"]
    | VoluntaryLifeBenefitsGrid["guaranteeIssueSpouseAgesDetails"],
): AgeBasedAmount[] {
  return (ageDetails || []).map((item) => ({
    ...(item.ageLower !== undefined ? { ageBandLower: item.ageLower } : {}),
    ...(item.ageUpper !== undefined ? { ageBandUpper: item.ageUpper } : {}),
    ...(item.amount !== undefined ? { amount: item.amount } : {}),
  }));
}

// The API models a list of amounts as the single `multipleOptions` type plus an
// `amounts` array; the grid splits that into its own type value so the editor
// knows to render a list of inputs. (The employee's benefit description also
// splits `salaryMultiplier` this way - spouse and child have no such type.)
const MULTIPLE_DOLLAR_AMOUNTS = "multipleDollarAmounts";

const toDependentApiType = (frontendType: string): string =>
  frontendType === MULTIPLE_DOLLAR_AMOUNTS
    ? RfpVoluntaryLifeSpouseChildBenefitTypeEnum.multipleOptions
    : frontendType;

const toDependentFrontendType = (
  apiType: string | undefined,
): string | undefined =>
  apiType === RfpVoluntaryLifeSpouseChildBenefitTypeEnum.multipleOptions
    ? MULTIPLE_DOLLAR_AMOUNTS
    : apiType;

function transformEmployeeBenefitDescription(
  benefits: VoluntaryLifeBenefitsGrid,
): NonNullable<VoluntaryLifeBenefitsApi["employee"]>["benefitDescription"] {
  if (!shouldIncludeBenefitInApi(benefits.benefitDescriptionEmployeeType)) {
    return undefined;
  }

  const frontendType = benefits.benefitDescriptionEmployeeType;
  let apiType = frontendType;

  if (frontendType === "multipleDollarAmounts") {
    apiType = RfpVoluntaryLifeEmployeeBenefitTypeEnum.multipleOptions;
  } else if (frontendType === "multipleSalaryMultipliers") {
    apiType = RfpVoluntaryLifeEmployeeBenefitTypeEnum.salaryMultiplier;
  }

  if (
    frontendType === "multipleDollarAmounts" ||
    frontendType === "multipleSalaryMultipliers" ||
    frontendType === RfpVoluntaryLifeEmployeeBenefitTypeEnum.multipleOptions
  ) {
    if (
      Array.isArray(benefits.benefitDescriptionEmployeeAmount) &&
      benefits.benefitDescriptionEmployeeAmount.length > 0
    ) {
      return {
        type: apiType,
        amounts: benefits.benefitDescriptionEmployeeAmount.map(
          (item: { amount: number }) => Number(item.amount),
        ),
      };
    }

    return undefined;
  }

  if (frontendType === NotDisclosed) {
    return { type: apiType };
  }

  const amountValue = Number(benefits.benefitDescriptionEmployeeAmount);
  if (!isNaN(amountValue) && amountValue > 0) {
    return {
      type: apiType,
      amounts: [amountValue],
    };
  }

  return undefined;
}

function transformSpouseBenefitDescription(
  benefits: VoluntaryLifeBenefitsGrid,
): NonNullable<VoluntaryLifeBenefitsApi["spouse"]>["benefitDescription"] {
  if (!shouldIncludeBenefitInApi(benefits.benefitDescriptionSpouseType)) {
    return undefined;
  }

  const benefitAmountDependent: {
    type: string;
    amounts?: number[];
  } = {
    type: toDependentApiType(benefits.benefitDescriptionSpouseType),
  };

  switch (benefits.benefitDescriptionSpouseType) {
    case RfpVoluntaryLifeSpouseChildBenefitTypeEnum.notIncluded:
      return benefitAmountDependent;
    case MULTIPLE_DOLLAR_AMOUNTS:
    case RfpVoluntaryLifeSpouseChildBenefitTypeEnum.multipleOptions:
      if (
        Array.isArray(benefits.benefitDescriptionSpouseAmount) &&
        benefits.benefitDescriptionSpouseAmount.length > 0
      ) {
        benefitAmountDependent.amounts =
          benefits.benefitDescriptionSpouseAmount.map((num) =>
            Number(num.amount),
          );
        return benefitAmountDependent;
      }
      return undefined;
    default: {
      const amountValue = Number(benefits.benefitDescriptionSpouseAmount);
      if (!isNaN(amountValue)) {
        benefitAmountDependent.amounts = [amountValue];
        return benefitAmountDependent;
      }

      return undefined;
    }
  }
}

function createChildBenefitAmountByAge(params: {
  type: ChildBenefitType;
  amount: ChildBenefitInputAmount;
  ageBandLower: number;
  ageBandUpper?: number;
  unit: "days" | "months";
}): ChildBenefitAmountByAge | undefined {
  if (!shouldIncludeBenefitInApi(params.type)) {
    return undefined;
  }

  const benefitAmountDependent: {
    ageBandLower: number;
    ageBandUpper?: number;
    unit: "days" | "months";
    benefitAmount: {
      type: string;
      amounts?: number[];
    };
  } = {
    ageBandLower: params.ageBandLower,
    ageBandUpper: params.ageBandUpper,
    unit: params.unit,
    benefitAmount: {
      type: toDependentApiType(params.type as string),
    },
  };

  switch (params.type) {
    case NotDisclosed:
    case RfpVoluntaryLifeSpouseChildBenefitTypeEnum.notIncluded:
      return benefitAmountDependent as ChildBenefitAmountByAge;
    case MULTIPLE_DOLLAR_AMOUNTS:
    case RfpVoluntaryLifeSpouseChildBenefitTypeEnum.multipleOptions:
      if (Array.isArray(params.amount) && params.amount.length > 0) {
        benefitAmountDependent.benefitAmount.amounts = params.amount.map(
          (num) => Number(num.amount),
        );
        return benefitAmountDependent as ChildBenefitAmountByAge;
      }
      return undefined;
    default: {
      const amountValue = Number(params.amount);
      if (!isNaN(amountValue)) {
        benefitAmountDependent.benefitAmount.amounts = [amountValue];
        return benefitAmountDependent as ChildBenefitAmountByAge;
      }

      return undefined;
    }
  }
}

function buildChildBenefitAmountsByAge(
  benefits: VoluntaryLifeBenefitsGrid,
): ChildBenefitAmountByAge[] {
  return [
    createChildBenefitAmountByAge({
      type: benefits.benefitDescriptionChildDependentAgesBirthTo14DaysType,
      amount: benefits.benefitDescriptionChildDependentAgesBirthTo14DaysAmount,
      ageBandLower: 0,
      ageBandUpper: 14,
      unit: "days",
    }),
    createChildBenefitAmountByAge({
      type: benefits.benefitDescriptionChildDependentAges15DaysTo6MonthsType,
      amount:
        benefits.benefitDescriptionChildDependentAges15DaysTo6MonthsAmount,
      ageBandLower: 14,
      ageBandUpper: 180,
      unit: "days",
    }),
    createChildBenefitAmountByAge({
      type: benefits.benefitDescriptionChildDependentAges6MonthsToLimitingAgeType,
      amount:
        benefits.benefitDescriptionChildDependentAges6MonthsToLimitingAgeAmount,
      ageBandLower: 6,
      ageBandUpper:
        benefits.childLimitingAge != null
          ? benefits.childLimitingAge * 12
          : undefined,
      unit: "months",
    }),
  ].filter(
    (benefitAmount): benefitAmount is ChildBenefitAmountByAge =>
      benefitAmount !== undefined,
  );
}

function transformChildStudentLimitingAge(
  benefits: VoluntaryLifeBenefitsGrid,
): NonNullable<VoluntaryLifeBenefitsApi["child"]>["studentLimitingAge"] {
  if (
    !shouldIncludeBenefitInApi(benefits.studentStatusStudentLimitingAgeType)
  ) {
    return undefined;
  }

  const studentType = benefits.studentStatusStudentLimitingAgeType;
  return {
    type: studentType,
    ...(studentType === "included" && {
      amount: benefits.studentStatusStudentLimitingAgeAmount ?? 1,
    }),
  };
}

function transformEmployeeBenefits(
  benefits: VoluntaryLifeBenefitsGrid,
): VoluntaryLifeBenefitsApi["employee"] | undefined {
  if (!hasEmployeeBenefits(benefits)) {
    return undefined;
  }

  const employee: NonNullable<VoluntaryLifeBenefitsApi["employee"]> = {};

  employee.benefitDescription = transformEmployeeBenefitDescription(benefits);

  if (shouldIncludeBenefitInApi(benefits.benefitMaximumEmployeeType)) {
    employee.benefitMaximum = {
      type: benefits.benefitMaximumEmployeeType,
      dollarAmount: benefits.benefitMaximumEmployeeDollarAmount,
      salaryMultiplier: benefits.benefitMaximumEmployeeSalaryMultiplier,
    };
  }

  if (
    benefits.guaranteeIssueEmployeeType ||
    benefits.guaranteeIssueEmployeeAgesDetails
  ) {
    employee.guaranteeIssue = {
      type: benefits.guaranteeIssueEmployeeType,
      dollarAmount: benefits.guaranteeIssueEmployeeDollarAmount,
      salaryMultiplier: benefits.guaranteeIssueEmployeeSalaryMultiplier,
      additionalAmount: benefits.guaranteeIssueEmployeeAdditionalAmount,
      ageBasedAmounts: mapAgeBasedAmounts(
        benefits.guaranteeIssueEmployeeAgesDetails,
      ),
    };
  }

  return employee;
}

function transformSpouseBenefits(
  benefits: VoluntaryLifeBenefitsGrid,
): VoluntaryLifeBenefitsApi["spouse"] | undefined {
  if (!hasSpouseBenefits(benefits)) {
    return undefined;
  }

  const spouse: NonNullable<VoluntaryLifeBenefitsApi["spouse"]> = {};

  spouse.benefitDescription = transformSpouseBenefitDescription(benefits);

  if (
    shouldIncludeBenefitInApi(benefits.benefitMaximumSpouseType) ||
    benefits.benefitMaximumSpouseAmount
  ) {
    spouse.benefitMaximum = {
      type: benefits.benefitMaximumSpouseType,
      dollarAmount: benefits.benefitMaximumSpouseAmount,
    };
  }

  if (
    shouldIncludeBenefitInApi(benefits.guaranteeIssueSpouseType) ||
    benefits.guaranteeIssueSpouseAgesDetails
  ) {
    spouse.guaranteeIssue = {
      type: benefits.guaranteeIssueSpouseType,
      dollarAmount: benefits.guaranteeIssueSpouseDollarAmount,
      salaryMultiplier: benefits.guaranteeIssueSpouseSalaryMultiplier,
      additionalAmount: benefits.guaranteeIssueSpouseAdditionalAmount,
      ageBasedAmounts: mapAgeBasedAmounts(
        benefits.guaranteeIssueSpouseAgesDetails,
      ),
    };
  }

  if (
    shouldIncludeBenefitInApi(benefits.amountNotToExceedSpouseType) ||
    benefits.amountNotToExceedSpouseAmount
  ) {
    spouse.amountNotToExceed = {
      type: benefits.amountNotToExceedSpouseType,
      amount: benefits.amountNotToExceedSpouseAmount,
    };
  }

  return spouse;
}

function transformChildBenefits(
  benefits: VoluntaryLifeBenefitsGrid,
  options?: BenefitTransformToApiOptions,
): VoluntaryLifeBenefitsApi["child"] | undefined {
  if (!hasChildBenefits(benefits)) {
    return undefined;
  }

  const child: NonNullable<VoluntaryLifeBenefitsApi["child"]> = {};

  // Both signals matter, not either alone:
  //  - Presence: a caller that never went through transformChildBenefitsFromApi
  //    at all (isolated calls, tests constructing a grid object by hand) has
  //    no childBenefitAmountsByAge key - always fall back to the fixed fields
  //    for those, whatever the caller asked for.
  //  - The caller's intent: transformChildBenefitsFromApi always attaches the
  //    array unconditionally once a real load happens, so presence alone can no
  //    longer distinguish "the analyst edited the flexible field" from "this is
  //    just what got attached on load." Present passes no options and gets the
  //    authoritative default (it has no fixed-field fallback); Build passes
  //    false while btx-ch-benefits-field is off, where the three fixed fields
  //    are what the analyst can actually edit even though the array is also on
  //    the row, unedited.
  const benefitAmountsByAge =
    benefits.childBenefitAmountsByAge !== undefined &&
    (options?.childAgeBandsAuthoritative ?? true)
      ? benefits.childBenefitAmountsByAge
      : buildChildBenefitAmountsByAge(benefits);

  if (benefitAmountsByAge.length > 0) {
    child.benefitAmountsByAge = benefitAmountsByAge;
  }

  if (
    shouldIncludeBenefitInApi(benefits.benefitMaximumChildType) ||
    benefits.benefitMaximumChildAmount
  ) {
    child.benefitMaximum = {
      type: benefits.benefitMaximumChildType,
      dollarAmount: benefits.benefitMaximumChildAmount,
    };
  }

  if (
    shouldIncludeBenefitInApi(benefits.amountNotToExceedChildType) ||
    benefits.amountNotToExceedChildAmount
  ) {
    child.amountNotToExceed = {
      type: benefits.amountNotToExceedChildType,
      amount: benefits.amountNotToExceedChildAmount,
    };
  }

  child.studentLimitingAge = transformChildStudentLimitingAge(benefits);

  return child;
}

function mapAmountOptions(amounts: number[]): AmountOption[] {
  return amounts.map((amount) => ({ amount }));
}

function mapAgeBasedAmountsFromApi(
  ageBasedAmounts:
    | NonNullable<
        NonNullable<EmployeeBenefitsApi["guaranteeIssue"]>["ageBasedAmounts"]
      >
    | NonNullable<
        NonNullable<SpouseBenefitsApi["guaranteeIssue"]>["ageBasedAmounts"]
      >
    | undefined,
): VoluntaryLifeBenefitsGrid["guaranteeIssueEmployeeAgesDetails"] {
  if (!ageBasedAmounts || ageBasedAmounts.length === 0) {
    return undefined;
  }

  return ageBasedAmounts.map((item) => ({
    ...(item.ageBandLower !== undefined ? { ageLower: item.ageBandLower } : {}),
    ...(item.ageBandUpper !== undefined ? { ageUpper: item.ageBandUpper } : {}),
    ...(item.amount !== undefined ? { amount: item.amount } : {}),
  }));
}

function transformEmployeeBenefitDescriptionFromApi(
  benefitDescription: EmployeeBenefitsApi["benefitDescription"],
): Partial<VoluntaryLifeBenefitsGrid> {
  if (!benefitDescription) {
    return {};
  }

  const apiType = benefitDescription.type;
  const amounts = benefitDescription.amounts;

  let frontendType = apiType;
  if (apiType === "multipleOptions") {
    frontendType = "multipleDollarAmounts";
  } else if (
    apiType === "salaryMultiplier" &&
    Array.isArray(amounts) &&
    amounts.length > 1
  ) {
    frontendType = "multipleSalaryMultipliers";
  }

  return {
    benefitDescriptionEmployeeType: frontendType,
    ...(Array.isArray(amounts)
      ? {
          benefitDescriptionEmployeeAmount:
            frontendType === "multipleDollarAmounts" ||
            frontendType === "multipleSalaryMultipliers"
              ? mapAmountOptions(amounts)
              : amounts[0],
        }
      : {}),
  };
}

function transformEmployeeBenefitsFromApi(
  employee: EmployeeBenefitsApi | undefined,
): Partial<VoluntaryLifeBenefitsGrid> {
  if (!employee) {
    return {};
  }

  return {
    ...transformEmployeeBenefitDescriptionFromApi(employee.benefitDescription),
    ...(employee.benefitMaximum
      ? {
          benefitMaximumEmployeeType: employee.benefitMaximum.type,
          benefitMaximumEmployeeDollarAmount:
            employee.benefitMaximum.dollarAmount,
          benefitMaximumEmployeeSalaryMultiplier:
            employee.benefitMaximum.salaryMultiplier,
        }
      : {}),
    ...(employee.guaranteeIssue
      ? {
          guaranteeIssueEmployeeType: employee.guaranteeIssue.type,
          guaranteeIssueEmployeeDollarAmount:
            employee.guaranteeIssue.dollarAmount,
          guaranteeIssueEmployeeSalaryMultiplier:
            employee.guaranteeIssue.salaryMultiplier,
          guaranteeIssueEmployeeAdditionalAmount:
            employee.guaranteeIssue.additionalAmount,
          guaranteeIssueEmployeeAgesDetails: mapAgeBasedAmountsFromApi(
            employee.guaranteeIssue.ageBasedAmounts,
          ),
        }
      : {}),
  };
}

function transformSpouseBenefitDescriptionFromApi(
  benefitDescription: SpouseBenefitsApi["benefitDescription"],
): Partial<VoluntaryLifeBenefitsGrid> {
  if (!benefitDescription) {
    return {};
  }

  return {
    benefitDescriptionSpouseType: toDependentFrontendType(
      benefitDescription.type,
    ),
    ...(Array.isArray(benefitDescription.amounts)
      ? {
          benefitDescriptionSpouseAmount:
            benefitDescription.type === "multipleOptions"
              ? mapAmountOptions(benefitDescription.amounts)
              : benefitDescription.amounts[0],
        }
      : {}),
  };
}

function transformSpouseBenefitsFromApi(
  spouse: SpouseBenefitsApi | undefined,
): Partial<VoluntaryLifeBenefitsGrid> {
  if (!spouse) {
    return {};
  }

  return {
    ...transformSpouseBenefitDescriptionFromApi(spouse.benefitDescription),
    ...(spouse.benefitMaximum
      ? {
          benefitMaximumSpouseType: spouse.benefitMaximum.type,
          benefitMaximumSpouseAmount: spouse.benefitMaximum.dollarAmount,
        }
      : {}),
    ...(spouse.guaranteeIssue
      ? {
          guaranteeIssueSpouseType: spouse.guaranteeIssue.type,
          guaranteeIssueSpouseDollarAmount: spouse.guaranteeIssue.dollarAmount,
          guaranteeIssueSpouseSalaryMultiplier:
            spouse.guaranteeIssue.salaryMultiplier,
          guaranteeIssueSpouseAdditionalAmount:
            spouse.guaranteeIssue.additionalAmount,
          guaranteeIssueSpouseAgesDetails: mapAgeBasedAmountsFromApi(
            spouse.guaranteeIssue.ageBasedAmounts,
          ),
        }
      : {}),
    ...(spouse.amountNotToExceed
      ? {
          amountNotToExceedSpouseType: spouse.amountNotToExceed.type,
          amountNotToExceedSpouseAmount: spouse.amountNotToExceed.amount,
        }
      : {}),
  };
}

function transformChildBenefitAmountRangeFromApi(
  ageRange: ChildBenefitAmountByAge,
): Partial<VoluntaryLifeBenefitsGrid> {
  if (!ageRange.benefitAmount) {
    return {};
  }

  const amountValue = Array.isArray(ageRange.benefitAmount.amounts)
    ? ageRange.benefitAmount.type === "multipleOptions"
      ? mapAmountOptions(ageRange.benefitAmount.amounts)
      : ageRange.benefitAmount.amounts[0]
    : undefined;

  const frontendType = toDependentFrontendType(ageRange.benefitAmount.type);

  if (ageRange.unit === "days" && ageRange.ageBandLower === 0) {
    return {
      benefitDescriptionChildDependentAgesBirthTo14DaysType: frontendType,
      ...(amountValue !== undefined
        ? {
            benefitDescriptionChildDependentAgesBirthTo14DaysAmount:
              amountValue,
          }
        : {}),
    };
  }

  // Plans saved before the second band started at day 14 store a lower bound
  // of 15; both mean the same 14-days-to-6-months field.
  const startsTheSecondDayBand =
    ageRange.ageBandLower === 14 || ageRange.ageBandLower === 15;

  if (ageRange.unit === "days" && startsTheSecondDayBand) {
    return {
      benefitDescriptionChildDependentAges15DaysTo6MonthsType: frontendType,
      ...(amountValue !== undefined
        ? {
            benefitDescriptionChildDependentAges15DaysTo6MonthsAmount:
              amountValue,
          }
        : {}),
    };
  }

  if (ageRange.unit === "months") {
    return {
      benefitDescriptionChildDependentAges6MonthsToLimitingAgeType:
        frontendType,
      ...(amountValue !== undefined
        ? {
            benefitDescriptionChildDependentAges6MonthsToLimitingAgeAmount:
              amountValue,
          }
        : {}),
      ...(ageRange.ageBandUpper != null
        ? { childLimitingAge: Math.ceil(ageRange.ageBandUpper / 12) }
        : {}),
    };
  }

  return {};
}

function transformChildBenefitsFromApi(
  child: ChildBenefitsApi | undefined,
): Partial<VoluntaryLifeBenefitsGrid> {
  if (!child) {
    return {};
  }

  const childBenefitAmounts = (child.benefitAmountsByAge || []).reduce(
    (accumulator, ageRange) => ({
      ...accumulator,
      ...transformChildBenefitAmountRangeFromApi(ageRange),
    }),
    {} as Partial<VoluntaryLifeBenefitsGrid>,
  );

  return {
    ...childBenefitAmounts,
    // Always attached, unconditionally - mirrors Present's own
    // attachChildBenefitAmountsByAge (mpx-quotes-grid), which never checks
    // any flag. Gating this on the flag was tried and reverted: this read
    // can run before Build's flag-setting component (PlanDesigner) has ever
    // mounted (e.g. WorkbenchBuild2 loads and transforms RFP data as
    // PlanDesigner's parent), so the flag can't be trusted here at read
    // time regardless of refresh. Whether the array or the three fixed
    // fields wins on save is decided later, at save time, by
    // transformChildBenefits - by then the flag's owner has always mounted.
    childBenefitAmountsByAge: child.benefitAmountsByAge ?? [],
    ...(child.benefitMaximum
      ? {
          benefitMaximumChildType: child.benefitMaximum.type,
          benefitMaximumChildAmount: child.benefitMaximum.dollarAmount,
        }
      : {}),
    ...(child.amountNotToExceed
      ? {
          amountNotToExceedChildType: child.amountNotToExceed.type,
          amountNotToExceedChildAmount: child.amountNotToExceed.amount,
        }
      : {}),
    ...(child.studentLimitingAge
      ? {
          studentStatusStudentLimitingAgeType: child.studentLimitingAge.type,
          studentStatusStudentLimitingAgeAmount:
            child.studentLimitingAge.amount,
        }
      : {}),
  };
}

function deriveSpouseBenefitsIncluded(
  benefits: VoluntaryLifeBenefitsApi,
  transformed: VoluntaryLifeBenefitsGrid,
): Partial<VoluntaryLifeBenefitsGrid> {
  if (!benefits.spouse || typeof benefits.spouse !== "object") {
    return {};
  }

  const spouseTypeFields = [
    transformed.benefitDescriptionSpouseType,
    transformed.benefitMaximumSpouseType,
    transformed.guaranteeIssueSpouseType,
    transformed.amountNotToExceedSpouseType,
  ];
  const definedSpouseTypes = spouseTypeFields.filter(
    (value) => value !== undefined && value !== null,
  );

  return definedSpouseTypes.length > 0 &&
    definedSpouseTypes.every((value) => value === "notIncluded")
    ? { spouseBenefitsIncluded: "no" }
    : {};
}

function deriveChildBenefitsIncluded(
  benefits: VoluntaryLifeBenefitsApi,
  transformed: VoluntaryLifeBenefitsGrid,
): Partial<VoluntaryLifeBenefitsGrid> {
  if (!benefits.child || typeof benefits.child !== "object") {
    return {};
  }

  const childTypeFields = [
    transformed.benefitDescriptionChildDependentAgesBirthTo14DaysType,
    transformed.benefitDescriptionChildDependentAges15DaysTo6MonthsType,
    transformed.benefitDescriptionChildDependentAges6MonthsToLimitingAgeType,
    transformed.benefitMaximumChildType,
    transformed.amountNotToExceedChildType,
  ];
  const definedChildTypes = childTypeFields.filter(
    (value) => value !== undefined && value !== null,
  );

  return definedChildTypes.length > 0 &&
    definedChildTypes.every((value) => value === "notIncluded")
    ? { childBenefitsIncluded: "no" }
    : {};
}

// Extended (non-v5) fields - CD-2071. Plain-string fields flatten 1:1 onto
// the grid; `spouseAgeBasisEeOrSpAge` is the one field whose grid name
// differs from the API's `spouseAgeBasis`.
type VolLifeExtendedGridFields = Pick<
  VoluntaryLifeBenefitsGrid,
  | "eligibilityHours"
  | "suicideExclusion"
  | "waiverOfPremiumEligibility"
  | "waiverOfPremiumWaitingPeriod"
  | "waiverOfPremiumDuration"
  | "airBag"
  | "commonCarrier"
  | "repatriation"
  | "seatBelt"
  | "annualAbilityToIncreaseBenefit"
>;

const VOL_LIFE_EXTENDED_FIELD_KEYS: (keyof VolLifeExtendedGridFields)[] = [
  "eligibilityHours",
  "suicideExclusion",
  "waiverOfPremiumEligibility",
  "waiverOfPremiumWaitingPeriod",
  "waiverOfPremiumDuration",
  "airBag",
  "commonCarrier",
  "repatriation",
  "seatBelt",
  "annualAbilityToIncreaseBenefit",
];

/**
 * Transforms Voluntary Life's extended (non-v5) fields from grid to API
 * format. Returns `undefined` unless at least one field is actually set, so
 * callers can omit `extended` entirely rather than sending an empty object.
 */
function transformVolLifeExtendedToApi(
  benefits: VoluntaryLifeBenefitsGrid,
): VolLifeExtendedBenefitApi | undefined {
  const extended: VolLifeExtendedBenefitApi = Object.fromEntries(
    VOL_LIFE_EXTENDED_FIELD_KEYS.map((key) => [key, benefits[key]]),
  );
  // These three are edited through a numeric cell (CD-2412), so AG Grid
  // commits a number even though the backend field is a Go string.
  extended.waiverOfPremiumEligibility = toExtendedStringField(
    benefits.waiverOfPremiumEligibility,
  );
  extended.waiverOfPremiumWaitingPeriod = toExtendedStringField(
    benefits.waiverOfPremiumWaitingPeriod,
  );
  extended.waiverOfPremiumDuration = toExtendedStringField(
    benefits.waiverOfPremiumDuration,
  );
  extended.spouseAgeBasis = benefits.spouseAgeBasisEeOrSpAge;
  extended.employeeAgeReductionScheduleType = benefits.ageReductionsEe;
  extended.spouseAgeReductionScheduleType = benefits.ageReductionsSp;
  extended.employeeAgeReductionSchedules = mapAgeReductionBandsToApi(
    benefits.employeeAgeReductionSchedules,
  ) as ReductionDetails[] | undefined;
  extended.spouseAgeReductionSchedules = mapAgeReductionBandsToApi(
    benefits.spouseAgeReductionSchedules,
  ) as ReductionDetails[] | undefined;

  const hasAnySet = Object.values(extended).some(
    (value) => value !== undefined && value !== "",
  );

  return hasAnySet ? extended : undefined;
}

/**
 * Transforms Voluntary Life's extended (non-v5) fields from API to grid
 * format. Every grid field is present (as undefined) even when `extended`
 * itself is absent, so a later edit to any other field doesn't leave a stale
 * extended value in the grid.
 */
function transformVolLifeExtendedFromApi(
  extended: VolLifeExtendedBenefitApi | undefined,
): Partial<VoluntaryLifeBenefitsGrid> {
  const flat = Object.fromEntries(
    VOL_LIFE_EXTENDED_FIELD_KEYS.map((key) => [
      key,
      extended?.[key] ?? undefined,
    ]),
  ) as VolLifeExtendedGridFields;

  return {
    ...flat,
    spouseAgeBasisEeOrSpAge: extended?.spouseAgeBasis ?? undefined,
    ageReductionsEe: extended?.employeeAgeReductionScheduleType ?? undefined,
    ageReductionsSp: extended?.spouseAgeReductionScheduleType ?? undefined,
    employeeAgeReductionSchedules: mapAgeReductionBandsFromApi(
      extended?.employeeAgeReductionSchedules,
    ),
    spouseAgeReductionSchedules: mapAgeReductionBandsFromApi(
      extended?.spouseAgeReductionSchedules,
    ),
  };
}

/**
 * Transform Voluntary Life benefits from grid format to API format
 *
 * Converts flat grid fields into nested API structures for:
 * - Employee benefits (benefitDescription, benefitMaximum, guaranteeIssue)
 * - Spouse benefits (benefitDescription, benefitMaximum, guaranteeIssue, amountNotToExceed)
 * - Child benefits (benefitAmountsByAge, benefitMaximum, amountNotToExceed, studentLimitingAge)
 * - Definition of earnings (type and average months)
 * - Age reduction schedule (type and schedule details)
 *
 * @param benefits - Grid format benefits
 * @returns API format benefits
 */
export function transformVoluntaryLifeBenefitsToApi(
  benefits: VoluntaryLifeBenefitsGrid,
  options?: BenefitTransformToApiOptions,
): VoluntaryLifeBenefitsApi {
  const transformed: VoluntaryLifeBenefitsApi = {
    objectType: "voluntaryLifeAdnd",
  };

  transformed.employee = transformEmployeeBenefits(benefits);
  transformed.spouse = transformSpouseBenefits(benefits);
  transformed.child = transformChildBenefits(benefits, options);

  // Transform DefinitionOfEarnings
  if (shouldIncludeBenefitInApi(benefits.definitionOfEarningsType)) {
    const isTypeWithoutAverageMonth = [
      DefinitionOfEarningsEnum.notApplicable,
      DefinitionOfEarningsEnum.notIncluded,
      DefinitionOfEarningsEnum.salary,
      DefinitionOfEarningsEnum.priorYearW2,
      DefinitionOfEarningsEnum.k1,
    ].includes(benefits.definitionOfEarningsType as DefinitionOfEarningsEnum);

    const averageMonth = benefits.definitionOfEarningsAverageMonths;
    const hasAverageMonth =
      typeof averageMonth === "number" && averageMonth >= 0;

    transformed.definitionOfEarnings =
      isTypeWithoutAverageMonth || !hasAverageMonth
        ? { type: benefits.definitionOfEarningsType }
        : { type: benefits.definitionOfEarningsType, averageMonth };
  }

  // Simple field mappings
  if (shouldIncludeBenefitInApi(benefits.adndIncluded)) {
    transformed.adndIncluded = benefits.adndIncluded;
  }
  if (shouldIncludeBenefitInApi(benefits.adndTiedToLifeElection)) {
    transformed.adndTiedToLife = benefits.adndTiedToLifeElection;
  }
  if (shouldIncludeBenefitInApi(benefits.conversion))
    transformed.conversion = benefits.conversion;
  if (shouldIncludeBenefitInApi(benefits.acceleratedDeathBenefit)) {
    transformed.acceleratedDeathBenefit = benefits.acceleratedDeathBenefit;
  }
  if (shouldIncludeBenefitInApi(benefits.waiverOfPremium))
    transformed.waiverOfPremium = benefits.waiverOfPremium;
  if (shouldIncludeBenefitInApi(benefits.annualOpenEnrollment)) {
    transformed.annualOpenEnrollment = benefits.annualOpenEnrollment;
  }

  // Portability - normalize old lowercase values to correct casing
  if (shouldIncludeBenefitInApi(benefits.portability)) {
    let portability = benefits.portability;
    // Handle legacy lowercase values
    if (portability === "includedWithEoi") portability = "includedWithEOI";
    if (portability === "includedWithoutEoi")
      portability = "includedWithoutEOI";
    transformed.portability = portability;
  }

  // Transform ageReductionSchedule from separate type and details fields
  if (benefits.ageReductionScheduleType !== undefined) {
    transformed.ageReductionSchedule = transformAgeReductionScheduleToApi(
      benefits.ageReductionScheduleType,
      benefits.ageReductionScheduleSchedules,
    ) as VoluntaryLifeBenefitsApi["ageReductionSchedule"];
  }

  // Extended (non-v5) fields - CD-2071
  transformed.extended = transformVolLifeExtendedToApi(benefits);

  // Filter to only valid fields for this LOC
  const filtered = { objectType: transformed.objectType } as Record<
    string,
    unknown
  >;
  for (const key of Object.keys(transformed)) {
    if (VOLUNTARY_LIFE_VALID_FIELDS.has(key)) {
      filtered[key] = transformed[key as keyof VoluntaryLifeBenefitsApi];
    }
  }

  return cleanupNotDisclosedBenefits(
    filtered as unknown as VoluntaryLifeBenefitsApi,
  );
}

/**
 * Transform Voluntary Life benefits from API format to grid format
 *
 * Converts nested API structures to flat grid fields
 *
 * @param benefits - API format benefits
 * @returns Grid format benefits
 */
export function transformVoluntaryLifeBenefitsFromApi(
  benefits: VoluntaryLifeBenefitsApi,
): VoluntaryLifeBenefitsGrid {
  const transformed: VoluntaryLifeBenefitsGrid = {
    objectType: "voluntaryLifeAdnd",
  };

  Object.assign(
    transformed,
    transformEmployeeBenefitsFromApi(benefits.employee),
    transformSpouseBenefitsFromApi(benefits.spouse),
    transformChildBenefitsFromApi(benefits.child),
  );

  // Transform DefinitionOfEarnings from API object to grid fields
  if (
    benefits.definitionOfEarnings &&
    typeof benefits.definitionOfEarnings === "object"
  ) {
    transformed.definitionOfEarningsType = benefits.definitionOfEarnings.type;
    transformed.definitionOfEarningsAverageMonths =
      benefits.definitionOfEarnings.averageMonth;
  }

  // Transform adndTiedToLife field name
  if (benefits.adndTiedToLife !== undefined) {
    transformed.adndTiedToLifeElection = benefits.adndTiedToLife;
  }

  // Transform ageReductionSchedule from API object to grid fields
  Object.assign(
    transformed,
    transformAgeReductionScheduleFromApi(benefits.ageReductionSchedule),
  );

  // Simple field mappings
  if (benefits.adndIncluded) transformed.adndIncluded = benefits.adndIncluded;
  if (benefits.conversion) transformed.conversion = benefits.conversion;
  if (benefits.acceleratedDeathBenefit) {
    transformed.acceleratedDeathBenefit = benefits.acceleratedDeathBenefit;
  }
  if (benefits.waiverOfPremium)
    transformed.waiverOfPremium = benefits.waiverOfPremium;
  if (benefits.annualOpenEnrollment) {
    transformed.annualOpenEnrollment = benefits.annualOpenEnrollment;
  }
  if (benefits.portability) transformed.portability = benefits.portability;

  // Extended (non-v5) fields - CD-2071
  Object.assign(
    transformed,
    transformVolLifeExtendedFromApi(benefits.extended),
  );

  // Derive spouseBenefitsIncluded from spouse type fields.
  // If the API returned spouse data and ALL type fields are "notIncluded", the user
  // previously toggled spouse benefits off - reflect that in the FE-only flag.
  Object.assign(
    transformed,
    deriveSpouseBenefitsIncluded(benefits, transformed),
  );

  // Derive childBenefitsIncluded from child type fields.
  Object.assign(
    transformed,
    deriveChildBenefitsIncluded(benefits, transformed),
  );

  return transformed;
}

/**
 * Grid keys that persist under `benefits.extended` - see
 * STD_EXTENDED_GRID_FIELD_KEYS. The age-basis and age-reduction keys are
 * spelled differently on the grid than in the API object, so they are listed
 * here alongside the flat 1:1 keys.
 */
export const VOL_LIFE_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set([
  ...VOL_LIFE_EXTENDED_FIELD_KEYS,
  "spouseAgeBasisEeOrSpAge",
  "ageReductionsEe",
  "ageReductionsSp",
  "employeeAgeReductionSchedules",
  "spouseAgeReductionSchedules",
]);
